import type { FC } from 'react';
import { Switch } from 'src/components/buttons-and-actions/switch';
import { Label } from 'src/components/form/label';
import { Tooltip, useTooltipControl } from 'src/components/overlay/tooltip';
import { type ModalApi, useModal2 } from 'src/hooks/use-modal';

export type OptSwitchProps = {
  label: string;
  tooltip: string;
  onChange: () => void;
  modal?: FC<ModalApi & { onChange: () => void }>;
  isChecked?: boolean;
  loading?: boolean;
};

/**
 * This component is intended to be used for a page wide opt-out _or_ opt-in switch.
 * This is usually used during Angular-to-React-migration phases.
 */
export const OptSwitch: FC<OptSwitchProps> = ({
  label,
  tooltip,
  onChange,
  modal,
  isChecked = true,
  loading = false,
}) => {
  if (modal) {
    return (
      <OptSwitchWithModal
        label={label}
        tooltip={tooltip}
        onChange={onChange}
        modal={modal}
        loading={loading}
      />
    );
  }

  return (
    <div className="grid grid-flow-col items-center justify-end gap-md">
      <Label>{label}</Label>

      <Tooltip content={tooltip} noTabIndex>
        {(targetProps) => (
          <Switch
            label={isChecked ? 'Deactivate New Design' : 'Activate New Design'}
            isChecked={isChecked}
            onClick={onChange}
            {...targetProps}
            className="block"
            loading={loading}
          />
        )}
      </Tooltip>
    </div>
  );
};

const OptSwitchWithModal: FC<{
  label: string;
  tooltip: string;
  onChange: () => void;
  modal: FC<ModalApi & { onChange: () => void }>;
  loading?: boolean;
}> = ({ label, tooltip, onChange, modal: Modal, loading = false }) => {
  const newDesignTooltipControl = useTooltipControl();

  const oldDesignModal = useModal2({
    modal(props) {
      return <Modal {...props} onChange={onChange} />;
    },
    trigger(props) {
      return (
        <Tooltip content={tooltip} controlOpen={newDesignTooltipControl.open}>
          {(targetProps) => (
            <Switch
              label="Deactivate New Design"
              isChecked
              {...props}
              {...targetProps}
              {...newDesignTooltipControl.handlers}
              className="block"
              loading={loading}
            />
          )}
        </Tooltip>
      );
    },
  });

  return (
    <div className="grid grid-flow-col items-center justify-end gap-md">
      {oldDesignModal.modal}
      <Label>{label}</Label>
      {oldDesignModal.trigger}
    </div>
  );
};
